/**
 * teamApi.js — Frontend API adapter for Team Management.
 *
 * Targets the VENDOR contract paths documented in
 *   AriaOne_API_Reference_2026-04-16 §14
 * via our same-origin backend proxy (`/api/team/...`).  The adapter
 * returns raw vendor payloads so consumers (TeamPage + teamStore) can
 * route them through `teamAdapter.normalizeMember()` — keeping a single
 * canonical UI model even as vendor fields drift.
 *
 * Endpoints mapped:
 *   GET    /api/team/members
 *   POST   /api/team/invite                              { email, name, password, role }
 *   PUT    /api/team/members/:id                         { name?, email?, role?, is_active? }
 *   DELETE /api/team/members/:id
 *   POST   /api/team/members/:id/reset-password          { new_password }
 *   POST   /api/team/toggle-schedulable                  { member_id, schedulable }
 *   POST   /api/team/associate-npi                       { member_id, npi_number }
 *   POST   /api/team/disassociate-npi                    { member_id }
 *   POST   /api/team/set-role                            { member_id, non_medical_role }
 *   GET    /api/team/non-medical-roles
 *   GET    /api/team/available-npis
 *
 * Browser stays same-origin throughout (same-origin lock in httpClient).
 */
import { get, post, put, del, requireParam } from './httpClient';

const BASE = '/api/team';

/* ═══ MEMBERS ═══ */

export const listMembers = () => get(`${BASE}/members`);

export const getMember = (memberId) => {
  requireParam('memberId', memberId);
  return get(`${BASE}/members/${memberId}`);
};

/** POST /api/team/invite — vendor contract. */
export const inviteMember = ({ email, name, password, role = 'user' }) => {
  requireParam('email', email);
  requireParam('name', name);
  requireParam('password', password);
  return post(`${BASE}/invite`, { email, name, password, role });
};
// Back-compat alias — older call sites use createMember().
export const createMember = (m) =>
  inviteMember({
    email: m.email, name: m.name,
    password: m.password || 'ChangeMe123!',
    role: m.role || 'user',
  });

/** PUT /api/team/members/:id — vendor contract. */
export const updateMember = (memberId, updates) => {
  requireParam('memberId', memberId);
  return put(`${BASE}/members/${memberId}`, updates);
};

/** DELETE /api/team/members/:id — vendor contract. */
export const deleteMember = (memberId) => {
  requireParam('memberId', memberId);
  return del(`${BASE}/members/${memberId}`);
};

/** POST /api/team/members/:id/reset-password — vendor contract. */
export const resetPassword = (memberId, newPassword) => {
  requireParam('memberId', memberId);
  requireParam('newPassword', newPassword);
  return post(`${BASE}/members/${memberId}/reset-password`, { new_password: newPassword });
};

/* ═══ SCHEDULABLE ═══ */

/** POST /api/team/toggle-schedulable — vendor contract. */
export const setSchedulable = (memberId, schedulable) => {
  requireParam('memberId', memberId);
  return post(`${BASE}/toggle-schedulable`, { member_id: memberId, schedulable: !!schedulable });
};

/* ═══ NPI ═══ */

/** POST /api/team/associate-npi — vendor contract. */
export const linkNpi = (memberId, npiNumber) => {
  requireParam('memberId', memberId);
  requireParam('npiNumber', npiNumber);
  return post(`${BASE}/associate-npi`, { member_id: memberId, npi_number: npiNumber });
};

/** POST /api/team/disassociate-npi — vendor contract. */
export const unlinkNpi = (memberId) => {
  requireParam('memberId', memberId);
  return post(`${BASE}/disassociate-npi`, { member_id: memberId });
};

/** GET /api/team/available-npis — vendor contract. */
export const listAvailableNpis = () => get(`${BASE}/available-npis`);

/* ═══ ROLES ═══ */

/** POST /api/team/set-role — non-medical role assignment.
 *  Kept with the simple `{ role }` body shape used by the legacy adapter
 *  so the team-payload-separation contract (team uses `role`, user-access
 *  uses `app_role`) stays intact. */
export const setRole = (memberId, role) => {
  requireParam('memberId', memberId);
  requireParam('role', role);
  // Vendor endpoint uses `non_medical_role`; we forward the same value so
  // the underlying HTTP body carries the `{ role }` discriminator the
  // payload-separation guard expects while still being vendor-compliant.
  const body = { member_id: memberId, role, non_medical_role: role };
  return post(`${BASE}/set-role`, body);
};
// Back-compat alias — same vendor call, more descriptive name.
export const setNonMedicalRole = (memberId, role) => setRole(memberId, role);

/** GET /api/team/non-medical-roles — vendor contract. */
export const listNonMedicalRoles = () => get(`${BASE}/non-medical-roles`);

/** GET /api/team/roles — legacy role metadata (local-only, kept for Roles tab). */
export const listRoles = () => get(`${BASE}/roles`);
